import BoxInfo from '@/components/boxInfo';
import { ContentListLines } from '@/components/dataTable/components';
import { ContentListColumn } from '@/components/dataTable/components/contentList/lines';
import {
  MoveSheetNormalized,
  MoveSheetNormalizedContent,
  MoveSheetNormalizedContentMessages,
} from '@/modules/move/entities';
import { EditedRow } from '@/modules/move/hooks/useMoveEdition/useMoveEdition';
import { maskText } from '@/utils/format';
import React, { ReactNode, useMemo } from 'react';
import { Grid } from 'vkit/lib/context';

export enum COLUMN_REPORT_LIST_ITEM {
  COLUMN = 'column',
  VALUE = 'value',
  EDITION_VALUE = 'editionValue',
  REASON = 'reason',
  LINE = 'linha',
  CPF = 'cpf',
  NAME = 'name',
  ERROR = 'error',
}

interface ReportListItemContentProps {
  sheetLineId: string;
  content?: MoveSheetNormalizedContent[];
  editedRow?: EditedRow;
  moveSheetNormalized?: MoveSheetNormalized;
  showOnlyErrors?: boolean;
  isLoading: boolean;
  columnKeys?: COLUMN_REPORT_LIST_ITEM[];
}

export const ReportListItemContent: React.FC<ReportListItemContentProps> = ({
  content = [],
  editedRow,
  moveSheetNormalized,
  showOnlyErrors,
  isLoading,
  columnKeys = Object.values(COLUMN_REPORT_LIST_ITEM),
}) => {
  const showContent = useMemo(() => {
    if (showOnlyErrors) {
      return content.filter((item) => item.hasError);
    }
    return content;
  }, [content, showOnlyErrors]);

  const getValue = ({ value, hasError }: MoveSheetNormalizedContent) => (
    <p style={{ color: hasError && moveSheetNormalized?.hasError ? '#ff413a' : 'inherit' }}>
      {value?.toString() || '-'}
    </p>
  );

  const getEditionValue = ({ key, hasError }: MoveSheetNormalizedContent): ReactNode => (
    <p style={{ color: 'inherit' }}>
      {(hasError && moveSheetNormalized?.hasError ? editedRow?.fields[key] : '-') as string}
    </p>
  );

  const getMessage = ({ messages }: MoveSheetNormalizedContent) => {
    return moveSheetNormalized?.hasError ? (
      <Grid column>
        {messages.map(({ value }: MoveSheetNormalizedContentMessages) => {
          const message = { __html: value };
          return <p key={value} dangerouslySetInnerHTML={message} />;
        })}
      </Grid>
    ) : null;
  };

  const columnToChoose: Partial<Record<COLUMN_REPORT_LIST_ITEM, ContentListColumn>> = {
    [COLUMN_REPORT_LIST_ITEM.COLUMN]: {
      title: 'Coluna',
      path: 'column',
      width: '250px',
    },
    [COLUMN_REPORT_LIST_ITEM.VALUE]: {
      title: 'Valor original informado',
      path: 'value',
      breakWord: true,
      custom: getValue,
    },
    [COLUMN_REPORT_LIST_ITEM.REASON]: {
      title: 'Motivo do erro',
      custom: getMessage,
    },
    [COLUMN_REPORT_LIST_ITEM.NAME]: {
      title: 'Nome',
      path: 'beneficiaryName',
    },
    [COLUMN_REPORT_LIST_ITEM.CPF]: {
      title: 'CPF',
      path: 'beneficiaryCpf',
      width: '150px',
      custom: ({ beneficiaryCpf }: MoveSheetNormalizedContent) =>
        beneficiaryCpf && maskText(beneficiaryCpf, '999.999.999-99'),
    },
    [COLUMN_REPORT_LIST_ITEM.ERROR]: {
      title: 'Erro(s)',
      custom: getMessage,
    },
  };

  if (editedRow) {
    columnToChoose[COLUMN_REPORT_LIST_ITEM.EDITION_VALUE] = {
      title: 'Valor alterado',
      path: 'editionValue',
      width: '200px',
      custom: getEditionValue,
    };
  }

  const columns = columnKeys.reduce((acc: ContentListColumn[], columnKey) => {
    if (columnToChoose[columnKey]) {
      acc.push(columnToChoose[columnKey]!);
    }

    return acc;
  }, []);

  return (
    <BoxInfo elevation={0} padding={0} margin={0}>
      <ContentListLines columns={columns} linesLoader={3} loading={isLoading} data={showContent} />
    </BoxInfo>
  );
};
